import { Button, Badge } from "@aix/ui"
import { X } from "lucide-react"
import dayjs from "dayjs"

interface WeeklyPlan {
  id: string
  weekNumber: number
  weekStartDate: string
  weekEndDate: string
  status: "upcoming" | "active" | "completed" | "skipped"
  phaseName: string | null
  expectations: any
  dailyStructure: any
  completionCount: number
  totalActions: number
  adjusted: boolean
  adjustmentReason: string | null
  originalPlan: any
  generatedAt: Date
  generatedBy: string
}

interface WeeklyPlanDetailsModalProps {
  plan: WeeklyPlan | null
  isOpen: boolean
  onClose: () => void
}

export default function WeeklyPlanDetailsModal({ plan, isOpen, onClose }: WeeklyPlanDetailsModalProps) {
  if (!isOpen || !plan) return null

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-primary text-primary-foreground"
      case "completed":
        return "bg-green-500 text-white"
      case "upcoming":
        return "bg-blue-500 text-white"
      case "skipped":
        return "bg-gray-500 text-white"
      default:
        return "bg-secondary text-secondary-foreground"
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Week {plan.weekNumber} Details</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {dayjs(plan.weekStartDate).format("MMM DD")} - {dayjs(plan.weekEndDate).format("MMM DD, YYYY")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={getStatusColor(plan.status)}>{plan.status}</Badge>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-secondary rounded-lg">
            <div>
              <p className="text-xs text-muted-foreground">Phase</p>
              <p className="text-sm font-medium text-foreground">{plan.phaseName || "No phase set"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Completion</p>
              <p className="text-sm font-medium text-foreground">
                {plan.completionCount} / {plan.totalActions} (
                {plan.totalActions > 0 ? Math.round((plan.completionCount / plan.totalActions) * 100) : 0}%)
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Generated</p>
              <p className="text-sm font-medium text-foreground">
                {dayjs(plan.generatedAt).format("MMM DD, YYYY HH:mm")}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Generation Mode</p>
              <p className="text-sm font-medium text-foreground capitalize">{plan.generatedBy.replace(/-/g, " ")}</p>
            </div>
          </div>

          {/* Adjusted Banner */}
          {plan.adjusted && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                  Adjusted
                </Badge>
                <span className="text-sm font-medium text-foreground">This plan has been adjusted</span>
              </div>
              {plan.adjustmentReason && <p className="text-sm text-muted-foreground">{plan.adjustmentReason}</p>}
            </div>
          )}

          {/* Expectations */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">Week Expectations</h3>
            {Array.isArray(plan.expectations) && plan.expectations.length > 0 ? (
              <ul className="space-y-2">
                {plan.expectations.map((expectation: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-3 p-3 bg-secondary rounded-lg">
                    <span className="text-primary font-semibold mt-0.5">{idx + 1}.</span>
                    <span className="text-foreground flex-1">{expectation}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground italic">No expectations set</p>
            )}
          </div>

          {/* Daily Structure */}
          {plan.dailyStructure && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Daily Structure</h3>
              <div className="bg-secondary rounded-lg p-4">
                {typeof plan.dailyStructure === "object" ? (
                  <div className="space-y-3">
                    {Object.entries(plan.dailyStructure).map(([day, structure]: [string, any]) => (
                      <div key={day} className="pb-3 border-b border-border last:border-b-0 last:pb-0">
                        <p className="font-semibold text-foreground capitalize mb-2">{day}</p>
                        {typeof structure === "string" ? (
                          <p className="text-sm text-muted-foreground">{structure}</p>
                        ) : (
                          <pre className="text-xs text-muted-foreground overflow-x-auto">
                            {JSON.stringify(structure, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <pre className="text-sm text-muted-foreground overflow-x-auto">
                    {JSON.stringify(plan.dailyStructure, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}

          {/* Original Plan (if adjusted) */}
          {plan.adjusted && plan.originalPlan && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Original Plan (Before Adjustment)</h3>
              <div className="bg-secondary rounded-lg p-4">
                {Array.isArray(plan.originalPlan) ? (
                  <ul className="space-y-2">
                    {plan.originalPlan.map((item: string, idx: number) => (
                      <li key={idx} className="text-sm text-muted-foreground">
                        {idx + 1}. {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <pre className="text-sm text-muted-foreground overflow-x-auto">
                    {JSON.stringify(plan.originalPlan, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}
